import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AgregarEvento from "../components/AgregarEvento";
import CalendarioEventos from "../components/CalendarioEvento";
import {Box, Typography, Stack, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Alert, IconButton} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventsApi from "../api/Api";
import EventCategoriesApi from "../api/eventCategoriesApi";
import {
  DEFAULT_EVENT_CATEGORIES,
  getDefaultEventCategoryColor,
} from "../constants/eventCategories";

function formatTimeHM(time) {
  if (!time) return "";
  return String(time).slice(0, 5);
}

function Pill({ children, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.1,
        bgcolor: "secondary.50",
        borderRadius: "33px",
        boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function CardShell({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function EventosPage() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventCategories, setEventCategories] = useState(DEFAULT_EVENT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await EventsApi.list();
      // Map backend data to frontend format
      const mappedEvents = data.map(event => ({
        ...event,
        titulo: event.title,
        fecha: event.startDate,
        horaInicio: formatTimeHM(event.startTime),
        horaFin: formatTimeHM(event.endTime),
        color: event.categoryColor || getDefaultEventCategoryColor(event.category)
      }));
      setEvents(mappedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEventCategories = useCallback(async () => {
    try {
      const data = await EventCategoriesApi.list();
      const items = Array.isArray(data) ? data : [];
      setEventCategories(items.length ? items : DEFAULT_EVENT_CATEGORIES);
    } catch (error) {
      console.error("Error fetching event categories:", error);
      setEventCategories(DEFAULT_EVENT_CATEGORIES);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchEventCategories();
  }, [fetchEvents, fetchEventCategories]);

  const handleDateSelect = (date) => {
    // We use a timestamp to ensure the prop change is detected even if the date is the same
    setSelectedDate({ date: date, timestamp: Date.now() });
    setSelectedEvent(null);
  };

  const handleEventAddedOrUpdated = () => {
    fetchEvents();
    setSelectedEvent(null);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    try {
      await EventsApi.remove(eventToDelete.id);
      setSnackbar({ open: true, message: "Evento eliminado con éxito", severity: "success" });
      fetchEvents();
      if (selectedEvent && selectedEvent.id === eventToDelete.id) {
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      setSnackbar({ open: true, message: "Error al eliminar el evento", severity: "error" });
    } finally {
      setEventToDelete(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        minHeight: "100%",
        overflowX: "hidden",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <IconButton
          onClick={() => navigate("/rrhh/dashboard")}
          sx={{
            width: 36,
            height: 36,
            bgcolor: "secondary.500",
            "&:hover": { bgcolor: "secondary.600" },
          }}
        >
          <ArrowBackIcon sx={{ color: "logoblanco", fontSize: 32 }} />
        </IconButton>

        <Stack sx={{ flex: 1, alignItems: "center", pr: "48px" }}>
          <Pill sx={{ bgcolor: "secondary.200" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: "primary.900" }}>
              Eventos
            </Typography>
          </Pill>
        </Stack>
      </Stack>

      <CardShell
        sx={{
          flex: 1,
          overflow: "visible",
          display: "flex",
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          sx={{ width: "100%" }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: 320 },
              flexShrink: 0,
              overflowY: "auto",
            }}
          >
            <AgregarEvento
              selectedDate={selectedDate}
              selectedEvent={selectedEvent}
              onEventAdded={handleEventAddedOrUpdated}
              onCancelEdit={() => setSelectedEvent(null)}
              onDeleteEvent={(event) => setEventToDelete(event)}
              eventCategories={eventCategories}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              position: "relative",
              display: "flex",
            }}
          >
            {loading && (
              <Box sx={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.5)',
                zIndex: 1,
                borderRadius: 2,
              }}>
                <CircularProgress />
              </Box>
            )}
            <CalendarioEventos
              events={events}
              onDateClick={handleDateSelect}
              onEventClick={handleEventClick}
            />
          </Box>
        </Stack>
      </CardShell>

      <Dialog open={!!eventToDelete} onClose={() => setEventToDelete(null)}>
        <DialogTitle>Eliminar Evento</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar el evento "{eventToDelete?.titulo}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventToDelete(null)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" sx={{ textTransform: "none" }}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
